import { Menu, Upload, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { LogOut, User, BarChart3, Building2, Settings, Receipt } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditsBalance } from "@/components/CreditsBalance";
import { supabase } from "@/integrations/supabase/client";
import { asBalanceResult } from "@/lib/rpcResults";
import { useToast } from "@/hooks/use-toast";
import * as Sentry from "@sentry/react";

interface MobileHeaderProps {
  onUploadClick: () => void;
  onSignOut: () => void;
  userEmail?: string;
}

export const MobileHeader = ({ onUploadClick, onSignOut, userEmail }: MobileHeaderProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleUploadClick = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: t('uploadArea.authRequired'),
          description: t('uploadArea.pleaseLogin'),
          variant: "destructive",
        });
        return;
      }

      const { data: balanceData, error: balanceError } = await supabase.rpc('check_balance', {
        p_user_id: user.id
      });

      if (balanceError) {
        console.error('Error checking balance:', balanceError);
        // Allow through if check fails
        onUploadClick();
        return;
      }

      const currentBalance = asBalanceResult(balanceData)?.balance ?? 0;

      if (currentBalance <= 0) {
        console.warn('[MobileHeader] User attempted upload with zero balance');

        const sentryEventId = Sentry.captureMessage(`User attempted upload with insufficient balance`, {
          level: 'warning',
          tags: {
            component: 'MobileHeader',
            action: 'mobile_upload_button',
            error_code: '2001', // INSUFFICIENT_CREDITS
          },
          extra: {
            userId: user.id,
            userEmail: user.email,
            currentBalance: currentBalance,
          },
        });

        const supportId = sentryEventId
          ? sentryEventId.substring(0, 8).toUpperCase()
          : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

        toast({
          title: t('uploadArea.insufficientBalance'),
          description: t('uploadArea.insufficientBalanceWithSupport', { supportId }),
          variant: "destructive",
          duration: Infinity, // Keep open until user closes
        });
        return;
      }

      onUploadClick();
    } catch (error) {
      console.error('Error in balance pre-check:', error);
      // Allow through if check fails
      onUploadClick();
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b bg-card shadow-sm">
      <div className="flex items-center justify-between px-4 py-3">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72">
            <SheetHeader>
              <SheetTitle className="text-left">{t('common.menu')}</SheetTitle>
            </SheetHeader>
            <div className="mt-6 flex flex-col gap-2">
              <Button
                variant="ghost"
                className="justify-start"
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => navigate('/'), 100);
                }}
              >
                <Home className="mr-3 h-4 w-4" />
                {t('navigation.home')}
              </Button>

              <Button
                variant="ghost"
                className="justify-start"
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => navigate('/?tab=receipts'), 100);
                }}
              >
                <Receipt className="mr-3 h-4 w-4" />
                {t('navigation.receipts')}
              </Button>

              <Button
                variant="ghost"
                className="justify-start"
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => navigate('/stores'), 100);
                }}
              >
                <Building2 className="mr-3 h-4 w-4" />
                {t('navigation.stores')}
              </Button>

              <Button variant="ghost" className="justify-start" disabled>
                <BarChart3 className="mr-3 h-4 w-4" />
                {t('navigation.analytics')}
                <span className="ml-auto text-xs text-muted-foreground">{t('common.comingSoon')}</span>
              </Button>

              <Button 
                variant="ghost" 
                className="justify-start"
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => navigate('/profile'), 100);
                }}
              >
                <span className="mr-3 text-sm truncate flex-1">{userEmail || "User"}</span>
                <Settings className="h-4 w-4" />
              </Button>

              <Button
                variant="ghost"
                className="justify-start text-destructive hover:text-destructive"
                onClick={onSignOut}
              >
                <LogOut className="mr-3 h-4 w-4" />
                {t('common.signOut')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        <h1
          className="text-lg font-bold bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
        >
          {t('common.appName')}
        </h1>

        <div className="flex items-center gap-2">
          <CreditsBalance />
          <Button
            size="icon"
            className="transition-all duration-300"
            onClick={handleUploadClick}
          >
            <Upload className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};
